import { sendGatheringCommand, subscribeSceneMessages, type GameSessionHandle } from '@sorrem-client/game/game-session-store';
import { GATHERING_PRELOAD_TAG } from '@sorrem-client/game/protocol';
import type { CrucibleGatheringRosterMemberState } from '@sorrem-client/game/render-config';
import { useSelectedCharacterId } from '@sorrem-client/ui/hooks';
import { getStoredAccountId } from '@sorrem-client/ui/shared/auth-session';
import { toRosterMemberKey } from '@sorrem-client/ui/shared/roster-member-key';
import { useGetCharactersQuery, useGetCrucibleModelManifestQuery } from '@sorrem-client/ui/store';
import { getStoredLoadout, listCharacterVisualItemIds } from '@sorrem/shared';
import { useEffect, useState } from 'react';

export interface RunModelPreload {
    /**
     * The run's models are loaded for the current party. If the server can't
     * list them this turns true anyway: the run then loads them itself as
     * it starts, as it always has.
     */
    readonly isRunLoaded: boolean;
    /** How much of the run's preloading is done, 0 to 1; 1 once `isRunLoaded`. */
    readonly runLoadProgress: number;
}

/** The preloads always sent: the player's own character and the run's models. */
const EXPECTED_PRELOADS = 2;

/**
 * Loads the run's models (its NPCs and props) into the crucible page's scene
 * while the party gathers, so the run starts with them already in place.
 * The player's own character loads first, as soon as the scene is up and
 * before the party state or the run's models arrive: every scene shows it,
 * and the gathering and the run share the scene's model caches, so this one
 * load serves both.
 */
export function useRunModelPreload(
    session: GameSessionHandle | undefined,
    crucibleId: string | null,
    roster: readonly CrucibleGatheringRosterMemberState[],
): RunModelPreload {
    /** Preloads sent, and answered; the run is loaded once every one sent has been. */
    const [preloads, setPreloads] = useState({ sent: 0, ready: 0 });

    useEffect(() => subscribeSceneMessages((message) => {
        if (message.type === 'run-models-ready') {
            setPreloads((current) => ({ ...current, ready: current.ready + 1 }));
        }
    }), []);

    const accountId = getStoredAccountId();
    const { selectedCharacterId } = useSelectedCharacterId();
    const { data: characters } = useGetCharactersQuery(accountId ?? '', { skip: accountId === null });
    const ownCharacter = characters?.find((character) => character.id === selectedCharacterId) ?? null;
    const ownModelId = ownCharacter?.characterModelId ?? null;
    const ownVisualItemsKey = ownCharacter ? listCharacterVisualItemIds(getStoredLoadout(ownCharacter), ownCharacter.outfit).sort().join('|') : '';
    useEffect(() => {
        if (session === undefined || ownModelId === null) {
            return;
        }
        const visualItemIds = ownVisualItemsKey === '' ? [] : ownVisualItemsKey.split('|');
        setPreloads((current) => ({ ...current, sent: current.sent + 1 }));
        sendGatheringCommand({
            type: 'preload-models',
            manifest: { characters: [{ characterModelId: ownModelId, visualItemIds }], propModels: [] },
            tag: GATHERING_PRELOAD_TAG,
        });
    }, [session, ownModelId, ownVisualItemsKey]);

    // The NPCs follow the party's strength, so a changed party asks again.
    const memberKey = roster.map(toRosterMemberKey).sort().join('|');
    const { data: manifest, isError: isManifestError } = useGetCrucibleModelManifestQuery(
        { crucibleId: crucibleId ?? '', memberKey },
        { skip: crucibleId === null || memberKey === '' },
    );
    useEffect(() => {
        if (session === undefined || manifest === undefined) {
            return;
        }
        setPreloads((current) => ({ ...current, sent: current.sent + 1 }));
        sendGatheringCommand({ type: 'preload-models', manifest, tag: GATHERING_PRELOAD_TAG });
    }, [session, manifest]);

    const isRunLoaded = isManifestError || (manifest !== undefined && preloads.sent > 0 && preloads.ready >= preloads.sent);
    const runLoadProgress = isRunLoaded ? 1 : Math.min(1, preloads.ready / Math.max(EXPECTED_PRELOADS, preloads.sent));
    return { isRunLoaded, runLoadProgress };
}
